/**
 * ListRow — one part rendered as a compact row in the list view. Left slot shows either a span tag
 * (accommodation/service) or a start/end time (timed); the right side shows the program-visibility eye and,
 * unless the cost type is None, the cost tag plus inline pax/group price inputs. Clicking the row opens the
 * part in the drawer.
 */
import { ChevronRight, Eye, EyeOff } from 'lucide-react';
import type { TFunction } from 'i18next';

import { PlaceholderCostType } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { TYPE_COLOR, TypeIcon, type BoardPart } from '../model';
import { CostTag } from './CostTag';
import { PriceInput } from './PriceInput';

type StyleVars = React.CSSProperties & Record<string, string | number>;

interface ListRowProps {
  t: TFunction;
  part: BoardPart;
  readOnly: boolean;
  /** Span tag text (e.g. "NOC") for accommodation/service rows; absent for timed rows. */
  tag?: string;
  sub: string;
  timeMain?: string;
  timeSub?: string;
  timeDur?: string;
  onOpen: (uid: string) => void;
  onUpdatePart: (uid: string, patch: Partial<BoardPart>) => void;
}

export function ListRow({
  t,
  part,
  readOnly,
  tag,
  sub,
  timeMain,
  timeSub,
  timeDur,
  onOpen,
  onUpdatePart,
}: ListRowProps) {
  const noCost = part.costType === PlaceholderCostType.None;
  const style: StyleVars = { '--act-color': TYPE_COLOR[part.type] };
  return (
    <div
      className={cn('tp-list-row', tag && 'tp-list-row--span')}
      style={style}
      onClick={() => onOpen(part.uid)}
      title={part.name}
    >
      <div className="tp-list-accent" aria-hidden="true" />
      <div className="tp-list-time">
        {tag ? (
          <span className="tp-list-tag">{tag}</span>
        ) : (
          <>
            <span className="tp-list-time-main">{timeMain}</span>
            {timeSub ? (
              <span className="tp-list-time-sub">
                {timeSub}
                {timeDur ? <span className="tp-list-time-dur"> · {timeDur}</span> : null}
              </span>
            ) : null}
          </>
        )}
      </div>
      <div className="tp-list-ico">
        <TypeIcon type={part.type} transportKind={part.transportKind} size={14} />
      </div>
      <div className="tp-list-info">
        <div className="tp-list-name">{part.name}</div>
        {sub ? <div className="tp-list-sub">{sub}</div> : null}
      </div>
      <span
        className={cn('tp-list-prog', !part.showInProgram && 'is-off')}
        title={part.showInProgram ? t('costScheme.inProgram') : t('costScheme.notInProgram')}
        aria-label={part.showInProgram ? t('costScheme.inProgram') : t('costScheme.notInProgram')}
      >
        {part.showInProgram ? <Eye size={15} /> : <EyeOff size={15} />}
      </span>
      {noCost ? (
        <div className="tp-list-costtag">
          <CostTag t={t} costType={part.costType} />
        </div>
      ) : (
        <div className="tp-list-prices">
          <div className="tp-list-costtag">
            <CostTag t={t} costType={part.costType} />
          </div>
          <PriceInput
            kind="pax"
            value={part.costPax}
            currency={part.currency}
            readOnly={readOnly}
            onChange={(value) => onUpdatePart(part.uid, { costPax: value })}
          />
          <PriceInput
            kind="group"
            value={part.costGroup}
            currency={part.currency}
            readOnly={readOnly}
            onChange={(value) => onUpdatePart(part.uid, { costGroup: value })}
          />
        </div>
      )}
      <span className="tp-list-chev" aria-hidden="true">
        <ChevronRight size={14} />
      </span>
    </div>
  );
}
